<script setup lang="ts">
  import {
    COLUMN_ORDER,
    EXCLUDED_PRODUCTS_KEYS,
    LABEL_OVERRIDES,
    type Product,
  } from '@/types/product'
  import {
    DropDown,
    Modal,
    UiButton,
    Table,
    FormInput,
    type Column,
    FormFieldContainer,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, ref, watch, type Ref } from 'vue'
  import IdfTagList from '@/components/idf-modal/IdfTagList.vue'
  import { useRdCodeStore } from '@/store/rd-code.store'
  import { createFlatBaseColumns } from '@/utils/table'
  import { type JourneyProduct } from '@/types/idf-journey-product'
  import type { IdfTag } from '@/types/idf-tag'
  import { useProductStore } from '@/store/product.store'
  import { storeToRefs } from 'pinia'
  import { useJourneyTemplateStore } from '@/store/journey-template.store'
  import { useJourneyProductValidation } from '@/composables/validation/use-journey-product-validation'

  const productStore = useProductStore()
  const { projectProducts } = storeToRefs(productStore)

  const rdCodeStore = useRdCodeStore()
  const { rdCodes } = storeToRefs(rdCodeStore)

  const journeyTemplateStore = useJourneyTemplateStore()
  const { journeyTemplate, journeyProducts } = storeToRefs(journeyTemplateStore)

  const error = ref(false)
  const errorMesssage = ref('')
  const idfTagList: Ref<IdfTag[]> = ref([])
  const show = ref(false)

  const localSelectedProducts = ref<Product[]>([])
  const localJourneyProduct: Ref<JourneyProduct[]> = ref([])
  const localJourneyProductColumns: Ref<Column[]> = ref([])

  const { validate, errorsForProduct, resetProductErrors } =
    useJourneyProductValidation(localJourneyProduct)

  const productModel = computed<Product | null>({
    get() {
      return null
    },

    set(newProduct: Product | null) {
      if (!newProduct) return

      const alreadyExists = localSelectedProducts.value.some(prod => prod.uuid === newProduct.uuid)
      if (alreadyExists) return

      parseJourneyTemplate(newProduct)
      localJourneyProductColumns.value = createFlatBaseColumns(
        localJourneyProduct.value,
        EXCLUDED_PRODUCTS_KEYS,
        {
          columnOrder: COLUMN_ORDER,
          labelOverrides: LABEL_OVERRIDES,
        }
      )
      localSelectedProducts.value = [newProduct, ...localSelectedProducts.value]
      idfTagList.value = createIdfTag(localSelectedProducts.value)
    },
  })

  function parseJourneyTemplate(product: Product) {
    const newEntry: JourneyProduct = {
      ...product,
      gewicht: null,
    }

    localJourneyProduct.value = [...localJourneyProduct.value, newEntry]
  }

  // For multiselect cases to prevent selecting duplicates
  const filteredProducts = computed(() => {
    if (!localSelectedProducts.value?.length) return projectProducts.value

    const excluded = new Set(localSelectedProducts.value.map(pprod => pprod.uuid))
    return projectProducts.value.filter(pprod => !excluded.has(pprod.uuid))
  })

  function createIdfTag(products: Product[]): IdfTag[] {
    return products.map<IdfTag>((lprod: Product) => {
      return {
        id: lprod.uuid,
        name: lprod.naam,
      }
    })
  }

  function applyWeightAndRdToProducts(products: Product[], journeyProducts: JourneyProduct[]) {
    for (const journeyProduct of journeyProducts) {
      if (!journeyProduct?.uuid) continue

      const product = products.find(prod => prod.uuid === journeyProduct.uuid)
      if (!product) continue

      product.gewicht = journeyProduct.gewicht ?? null
      product.rdCode = journeyProduct.rdCode ?? null
    }
  }

  function submit() {
    if (localJourneyProduct.value.length < 1) {
      error.value = true
      errorMesssage.value = 'Voeg een afvalstof toe'
      return
    }

    if (validate()) {
      journeyTemplateStore.convertProductsToApiProducts(localSelectedProducts.value)
      applyWeightAndRdToProducts(localSelectedProducts.value, localJourneyProduct.value)
      journeyTemplateStore.updateJourneyTemplateProduct(localJourneyProduct.value)
      journeyTemplateStore.updateJourneyTemplate({ materialen: localSelectedProducts.value })

      show.value = false
      error.value = false
      errorMesssage.value = ''
      resetProductErrors()
    } else {
      error.value = true
      errorMesssage.value = 'Vul alle gegevens aan'
    }
  }

  function onClose(close: () => void) {
    clearProducts()
    resetProductErrors()
    close()
  }

  const productTags = computed(() => {
    const tags: IdfTag[] = localSelectedProducts.value.map(product => ({
      id: product.uuid,
      name: `${product.naam!} - ${product.euralCode?.code}`,
    }))
    return tags
  })

  function removeProductTag(uuid: string) {
    localSelectedProducts.value = [
      ...localSelectedProducts.value.filter(selectedCarrier => selectedCarrier.uuid !== uuid),
    ]

    localJourneyProduct.value = [
      ...localJourneyProduct.value.filter(afvalstof => afvalstof.uuid !== uuid),
    ]

    journeyProducts.value = [...localJourneyProduct.value]
    journeyTemplate.value.materialen = [...journeyProducts.value]
  }

  function clearProducts() {
    const idsToRemove: string[] = []

    for (let index = 0; index < localSelectedProducts.value.length; index++) {
      const product = localSelectedProducts.value[index]
      if (product !== undefined) {
        const isSaved = journeyProducts.value.filter(saved => saved.uuid === product.uuid)

        if (isSaved.length > 0) {
          continue
        } else {
          idsToRemove.push(product.uuid)
        }
      }
    }

    for (let index = 0; index < idsToRemove.length; index++) {
      removeProductTag(idsToRemove[index] as keyof Product)
    }

    error.value = false
    errorMesssage.value = ''
  }

  watch(
    localJourneyProduct,
    () => {
      error.value = false
      errorMesssage.value = ''
    },
    { deep: true }
  )

  watch(journeyProducts, () => {
    localJourneyProduct.value = [...journeyProducts.value]
    localSelectedProducts.value = [...journeyProducts.value]
    localJourneyProductColumns.value = createFlatBaseColumns(
      localJourneyProduct.value,
      EXCLUDED_PRODUCTS_KEYS,
      {
        columnOrder: COLUMN_ORDER,
      }
    )
    idfTagList.value = createIdfTag(localSelectedProducts.value)
  })
</script>

<template>
  <div>
    <UiButton
      label="Afvalstoffen toevoegen"
      class="afvalstof-btn"
      variant="prominent"
      @click="show = !show"
      form="ignore-form"
    />
    <Modal v-model:show="show">
      <template #header>Afvalstoffen toevoegen</template>
      <template #default>
        <IdfTagList
          v-if="localSelectedProducts.length > 0"
          :tags="productTags"
          label="afvalstoffen"
          :remove-tag="removeProductTag"
        />
        <DropDown v-model="productModel" :data="filteredProducts" label="Afvalstoffen" required>
          <template #item="{ item }">{{ item.naam }} {{ item.euralCode?.code }}</template>
        </DropDown>

        <Table :rows="localJourneyProduct" :columns="localJourneyProductColumns" class="deep-table">
          <template #cell-euralCode="{ row }">
            <div>{{ row['euralCode']?.code }}</div>
          </template>
          <template #cell-gewicht="{ row }">
            <FormInput
              v-model="row['gewicht']"
              type="number"
              :postfix="row['eenheid'] ?? 'ton'"
              :error="errorsForProduct(row['uuid']).gewicht ?? ''"
            />
          </template>
          <template #cell-rdCode="{ row }">
            <FormFieldContainer :error="errorsForProduct(row['uuid']).rdCode ?? ''">
              <!-- custom select to escape modal with dropdown menu -->
              <select
                v-model="row['rdCode']"
                class="rd-select"
                :class="{ 'rd-error': errorsForProduct(row['uuid']).rdCode }"
              >
                <option :value="null" disabled>Selecteer een waarde</option>
                <option v-for="item in rdCodes" :key="item.id" :value="item">
                  {{ item.code }}
                </option>
              </select>
            </FormFieldContainer>
          </template>
        </Table>
      </template>
      <template #footer="{ close }">
        <div v-if="error" class="error">{{ errorMesssage }}</div>
        <UiButton @click.prevent="onClose(close)" label="Sluiten" variant="default" />
        <UiButton @click.prevent="submit" label="Opslaan" variant="prominent" />
      </template>
    </Modal>
  </div>
</template>

<style scoped>
  .afvalstof-btn {
    justify-self: right;
  }

  .error {
    color: var(--colours-support-negative);
    font-size: 1rem;
    flex-grow: 1;
  }

  .rd-select {
    width: 100%;
    height: var(--components-component-height);
    padding: var(--spacing-xs);
    border: 1px solid var(--colours-grey-3);
    border-radius: var(--components-form-field-corners);
    background: var(--colours-grey-1);
    font-size: 1rem;
    cursor: pointer;
    appearance: none;
    background-repeat: no-repeat;
    background-position: right var(--spacing-xs) center;
    padding-right: 2rem;
  }

  .rd-select:hover {
    background-color: var(--colours-support-hover);
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.08);
  }

  .rd-select:focus {
    outline: none;
    box-shadow: none;
    border: 2px solid var(--colours-support-keyboard-focus);
  }

  .rd-error {
    border: 2px solid var(--colours-support-error);
  }

  :deep(.form-field-container) {
    margin-bottom: 0;
  }

  :deep(.deep-table) {
    width: 100%;
  }
</style>
